/**
 * Passwort-Streckung im Browser.
 *
 * Warum nicht auf dem Server: der Workers-Free-Plan gibt 10 ms CPU pro
 * Request. Gemessen in workerd kosten 100 000 PBKDF2-Iterationen bereits
 * 9 ms auf schneller Hardware — auf einem geteilten Workers-Kern deutlich
 * mehr. scrypt liegt bei ~200 ms. Serverseitiges Stretching und der
 * Free-Plan schliessen sich aus.
 *
 * Deshalb streckt der Browser: PBKDF2-HMAC-SHA256 mit 600 000 Iterationen
 * (OWASP-Empfehlung). Dort gibt es weder ein Cloudflare-CPU-Budget noch den
 * workerd-Deckel von 100 000 Iterationen.
 *
 * Der Arbeitsfaktor bleibt dabei vollstaendig erhalten: Wer die Datenbank
 * erbeutet, findet dort nur einen billigen Hash ueber den abgeleiteten
 * Schluessel. Um an das Passwort zu kommen, muss er pro Rateversuch
 * dieselben 600 000 Iterationen rechnen — der Salt steht zwar in der
 * Datenbank, aber das aendert daran nichts.
 *
 * Was der Ansatz NICHT leistet: Der abgeleitete Schluessel ist auf dem
 * Transportweg passwortaequivalent. Das ist er beim Klartextpasswort aber
 * genauso, TLS schuetzt in beiden Faellen gleich.
 *
 * Dieses Modul laeuft unveraendert im Browser, in Node (Seed-Skript) und im
 * Worker — es benutzt ausschliesslich WebCrypto.
 */

/** OWASP-Empfehlung fuer PBKDF2-HMAC-SHA256. Steht je Nutzer in der DB. */
export const KDF_ITERATIONEN = 600_000;

export function b64(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s);
}

export function vonB64(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  // Explizit ueber einen ArrayBuffer: sonst tippt TS auf ArrayBufferLike,
  // das auch SharedArrayBuffer einschliesst und als BufferSource ausscheidet.
  const u8 = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

/**
 * Leitet aus Passwort und Salt den Schluessel ab, der an den Server geht.
 *
 * NFKC-Normalisierung vor dem Ableiten: sonst scheitert der Login je nach
 * Tastaturbelegung und Betriebssystem an sonst identischen Umlauten.
 */
export async function leiteSchluesselAb(
  passwort: string,
  saltB64: string,
  iterationen: number,
): Promise<string> {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passwort.normalize("NFKC")),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: vonB64(saltB64), iterations: iterationen },
    material,
    256,
  );
  return b64(bits);
}

export function neuerSalt(): string {
  return b64(crypto.getRandomValues(new Uint8Array(16)));
}
